import { MAP_SIZE } from "./Iso";

/**
 * Soft-collision layer for NPCs.
 * Tracks which tiles are currently claimed by an NPC so they don't overlap.
 * Unlike CollisionMap (hard blocks), occupancy is temporary and per-entity.
 */
export class OccupancyMap {
  /** Maps "x,y" → owner id. Only one owner per tile. */
  private readonly owners = new Map<string, string>();

  private tileKey(x: number, y: number): string {
    return `${Math.floor(x)},${Math.floor(y)}`;
  }

  /** Claim a tile for an entity. Releases any previous tile held by this id. */
  claim(id: string, x: number, y: number): void {
    // Release previous tile
    this.release(id);
    const key = this.tileKey(x, y);
    this.owners.set(key, id);
  }

  /** Release whatever tile this entity currently holds. */
  release(id: string): void {
    for (const [key, owner] of this.owners) {
      if (owner === id) {
        this.owners.delete(key);
        return;
      }
    }
  }

  /** Is this tile occupied by anyone other than `excludeId`? */
  isOccupied(x: number, y: number, excludeId?: string): boolean {
    const key = this.tileKey(x, y);
    const owner = this.owners.get(key);
    if (!owner) return false;
    return owner !== excludeId;
  }

  /** Is this tile free for `id` to move into? */
  canMoveTo(id: string, x: number, y: number): boolean {
    return !this.isOccupied(x, y, id);
  }

  clear(): void {
    this.owners.clear();
  }
}
